import { Field, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, TouchableOpacity } from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import { EmailAvatar } from '@components/Avatar/EmailAvatar';
import { ModalHeader } from '@components/Headers/ModalHeader';
import { ContactType } from '@components/Invite/Contact';
import ContactList from '@components/Invite/ContactList';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import SearchInput from '@components/shared/SearchInput/SearchInput';
import TextField from '@components/shared/TextField/TextField';
import { ChatDrawerScreen } from '@components/Web/Drawer/WebDrawerContext';
import {
  useCreateChatMutation,
  useListContactUsersQuery,
  useListUsersLazyQuery,
  User,
} from '@graphql/generated';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { FormValues } from '@navigation/chats/create-group-stack';
import { useAppNavigation } from '@navigation/useAppNavigation';
import {
  createGroupSchema,
  MAX_GROUP_NAME_LENGTH,
} from '@schemas/createGroupSchema';
import { convertUsersToContacts } from '@utils/convertUsersToContacts';
import { getSuggestionEmail } from '@utils/emailSuggestion';

export const CreateGroupChat: React.FC = () => {
  const { navigateToChatDrawer } = useAppNavigation();
  const [listUsers, { data: taskTagUsers }] = useListUsersLazyQuery();
  const { data: listUsersData, loading: listUsersLoading } =
    useListContactUsersQuery({
      variables: { term: '' },
    });

  const { listContactUsers } = listUsersData || { listContactUsers: [] };
  // TA-10279: this query's `loading` was previously dropped entirely — the
  // contact list rendered empty (no spinner, no skeleton) for the whole
  // first fetch. 150ms minimum hold per the global skeleton rule.
  const showContactsSkeleton = useMinimumVisible(
    listUsersLoading && listContactUsers.length === 0
  );

  const listUsersToContacts = convertUsersToContacts(
    listContactUsers as User[]
  );

  const sortedListUsersToContacts = [...listUsersToContacts].sort((a, b) =>
    a.firstName?.localeCompare(b.firstName)
  );

  const listUsersToContactType = convertUsersToContacts(
    (taskTagUsers?.listUsers as User[]) || []
  ).filter((u) => !listUsersToContacts.some((c) => c.id === u.id));

  const { t } = useTranslation();
  const toast = useToast();
  const initialValues: FormValues = {
    emails: [],
    name: '',
    users: [],
  };
  const [search, setSearch] = useState('');
  const [
    createChat,
    { data: createChatData, error: createChatError, loading: isCreatingChat },
  ] = useCreateChatMutation();

  useEffect(() => {
    if (!createChatData) return;
    navigateToChatDrawer({
      screen: ChatDrawerScreen.details,
      activeChat: createChatData.createChat,
    });
  }, [createChatData]);

  useEffect(() => {
    if (!createChatError) return;
    toast.hideAll();
    toast.show('Failed to create group, please try again later.', {
      duration: 3000,
    });
  }, [createChatError]);
  const submit = (values: FormValues) => {
    const { users, name, emails } = values;
    const userIds = users.map((u) => u.id);
    createChat({
      variables: {
        attributes: {
          emails,
          name,
          userIds,
          groupChat: true,
        },
      },
    });
  };

  useEffect(() => {
    search && listUsers({ variables: { term: search } });
  }, [search]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => submit(values)}
      validationSchema={createGroupSchema}>
      {({ values, submitForm, errors, setFieldValue, setFieldTouched }) => {
        const { users, emails } = values;
        const suggestedEmail = getSuggestionEmail(search);
        const suggestedEmailLower = suggestedEmail.toLowerCase();
        const hasSelectedSuggestedEmail = emails.some(
          (email) => email.toLowerCase() === suggestedEmailLower
        );
        const hasUserWithSuggestedEmail = [...sortedListUsersToContacts].some(
          (contact) =>
            (contact.email || '').toLowerCase() === suggestedEmailLower
        );
        const showEmailSuggestion =
          suggestedEmail.length > 0 &&
          !hasSelectedSuggestedEmail &&
          !hasUserWithSuggestedEmail;
        const selectedMembersCount = users.length + emails.length;

        return (
          <Box flex={1}>
            <Box p='m' borderBottomColor='grey02' borderBottomWidth={1}>
              <ModalHeader
                onCancel={() =>
                  navigateToChatDrawer({ screen: ChatDrawerScreen.newChat })
                }
                cancelColor='greenSecondary'
                title={t('models:chat.labels.newGroupChat')}
                onSubmit={submitForm}
                submitLabel={t('forms:Confirm')}
                // TA-10279: previously no loading state at all — double-submit
                // was possible while the mutation was in flight.
                isLoading={isCreatingChat}
              />
            </Box>
            <Box mx='m' mt='m' flex={1}>
              <Text marginBottom='m' variant='labelSmall'>
                {t('models:chat.labels.groupChatName')}
              </Text>
              <Field
                component={TextField}
                name='name'
                showCharsRemaining
                textInputProps={{ maxLength: MAX_GROUP_NAME_LENGTH }}
                placeholder={t('models:chat.placeholders.groupChatName')}
              />

              <Text variant='labelSmall' color='textPrimary'>
                {t('models:chat.labels.groupMembersWithCount', {
                  count: selectedMembersCount,
                })}
              </Text>
              <Box mt='m' flexDirection='column' flex={1}>
                <Box flexDirection='row' alignContent='center'>
                  <SearchInput onTextChanged={setSearch} value={search} />
                </Box>

                {showEmailSuggestion && (
                  <TouchableOpacity
                    accessibilityLabel={`Add email ${suggestedEmail}`}
                    onPress={() => {
                      setFieldTouched('emails');
                      setFieldValue('emails', [...emails, suggestedEmail]);
                      setSearch('');
                    }}>
                    <Box
                      mt='m'
                      p='m'
                      borderWidth={1}
                      borderColor='grey02'
                      borderRadius='s'
                      flexDirection='row'
                      alignItems='center'>
                      <EmailAvatar width={40} />
                      <Box ml='s' flex={1}>
                        <Text variant='labelEmphasized' color='textPrimary'>
                          {suggestedEmail}
                        </Text>
                        <Text variant='bodySecondary' color='textSecondary'>
                          Add by email
                        </Text>
                      </Box>
                    </Box>
                  </TouchableOpacity>
                )}

                <Box mt='m' flex={1}>
                  <ScrollView>
                    <ContactList
                      contacts={sortedListUsersToContacts}
                      taskTagUsers={search ? listUsersToContactType : undefined}
                      filterVal={search}
                      onPress={(item: ContactType) => {
                        setFieldTouched('users');

                        if (users?.find((u) => u.id === item.id)) {
                          const selectedUsers = users.filter(
                            (u) => u.id !== item.id
                          );
                          setFieldValue('users', selectedUsers);
                        } else {
                          setFieldValue('users', [...users, item]);
                        }
                      }}
                      isEditing
                      selected={users.map((u) => u.id)}
                      footerSpace={0}
                      loading={showContactsSkeleton}
                      showLoadingSkeleton
                    />
                  </ScrollView>
                </Box>
                <Box marginTop='m' maxHeight='50%'>
                  <ScrollView>
                    <Box flexDirection='row' flexWrap='wrap'>
                      {emails.map((email) => {
                        return (
                          <Box
                            key={email}
                            alignItems='center'
                            alignSelf='flex-start'
                            marginRight='s'
                            marginBottom='xs'
                            width={56}>
                            <EmailAvatar
                              width={40}
                              onDismiss={() => {
                                setFieldValue(
                                  'emails',
                                  emails.filter((item) => item !== email)
                                );
                              }}
                            />
                            <Text
                              variant='metadata'
                              marginTop='xs'
                              style={{ maxWidth: 56 }}
                              numberOfLines={1}>
                              {email}
                            </Text>
                          </Box>
                        );
                      })}
                      {users.map((user) => {
                        const { name, avatar, id, firstName } = user;
                        return (
                          <Box
                            key={id}
                            alignItems='center'
                            alignSelf='flex-start'
                            marginRight='s'
                            marginBottom='xs'
                            width={56}>
                            <Avatar
                              avatar={avatar}
                              label={name}
                              onDismiss={() => {
                                const newUsers = users.filter(
                                  (u) => u.id !== id
                                );
                                setFieldValue('users', newUsers);
                              }}
                            />
                            <Text
                              variant='metadata'
                              marginTop='xs'
                              style={{ maxWidth: 56 }}
                              numberOfLines={1}>
                              {firstName}
                            </Text>
                          </Box>
                        );
                      })}
                    </Box>
                  </ScrollView>
                </Box>
              </Box>
              {errors.users && (
                <Box flexDirection='row' alignItems='center'>
                  <Icon
                    name='AlertTriangle'
                    color='alertRed'
                    variant='s'
                    marginRight='xxs'
                  />
                  <Text variant='error'>{errors.users.toString()}</Text>
                </Box>
              )}
            </Box>
          </Box>
        );
      }}
    </Formik>
  );
};
